<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import { useTilesStore } from "@/stores/tiles";
import { useSettingsStore } from "@/stores/settings";

import { StringHelper } from "@/helpers/utilities/StringHelper";
import TilesGrid from "@/components/Tiles/TilesGrid.vue";
import LocalizeWord from "@/components/Shared/LocalizeWord.vue";
import ConfidentialPeopleSearch from "@/components/People/ConfidentialPeopleSearch.vue";

interface Props {
  groupId?: string | null;
}

const props = withDefaults(defineProps<Props>(), { groupId: null });

const route = useRoute();
const settingsStore = useSettingsStore();
const tilesStore = useTilesStore();

const catalog = ref<{ groups: any[] }>({ groups: [] });

const collectionId = computed(() => props.groupId || "UserMainTiles");
const tiles = computed(() => tilesStore.tiles(collectionId.value));

const todayLong = computed(() =>
  StringHelper.capitalize(
    new Date().toLocaleDateString(settingsStore.browserLocale, settingsStore.localeFormats.veryLongDateFormOptions)
  )
);

const todayShort = computed(() =>
  StringHelper.capitalize(
    new Date().toLocaleDateString(settingsStore.browserLocale, settingsStore.localeFormats.longDateFormOptions)
  )
);

const title = computed(() => tilesStore.groupTitle(collectionId.value));

const loadTiles = async (id: string) => {
  await tilesStore.loadTiles(id);
};

watch(
  () => route.params,
  () => {
    loadTiles(collectionId.value);
  }
);

onMounted(() => {
  loadTiles(collectionId.value);
});
</script>

<template>
  <div class="home">
    <div class="justify-between items-center hidden md:flex">
      <h1 class="text-xl md:text-3xl flex-grow flex-1">
        <LocalizeWord :value="title"></LocalizeWord>
      </h1>

      <ConfidentialPeopleSearch class="flex-grow flex-1"></ConfidentialPeopleSearch>

      <div v-if="!groupId" class="hidden xl:block flex-grow flex-1 text-right">{{ todayLong }}</div>
      <div v-if="!groupId" class="hidden md:block xl:hidden flex-grow flex-1 text-right">{{ todayShort }}</div>
    </div>

    <div class="lg:mt-12">
      <TilesGrid
        v-if="tiles.length > 0"
        :key="tiles.length"
        :tiles="tiles"
        :type-name="collectionId"
        :headline="''"
        :sort="catalog.groups.length > 0"
      />
    </div>
  </div>
</template>
